import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  CoreQuestionsFactoryService,
  FormQuestionsService,
  CountriesService,
  AgentsCacheService,
  ProfileService,
  CoursesService,
  UtilsQuestionsService
} from 'core/services';
import {
  QuestionBase,
  TextboxQuestion,
  QuestionGroup,
  DropdownQuestion,
  CheckboxQuestion,
  TagsQuestion,
  DatetimeQuestion,
  Decorator,
  NumberQuestion,
  RangeQuestion,
  HiddenQuestion,
  DocumentsUploader
} from 'core/components/forms';
import { DateTime, Info } from 'luxon';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { ROLES } from 'core/services/assets/constants';

@Injectable()
export class GroupDataQuestionsService implements FormQuestionsService {
  private DEFAULT_MIN_WEEKS = 3;
  private DEFAULT_MAX_WEEKS = 8;
  private WEEKS_IN_A_YEAR = 52;
  private RANKING_CONFIGS = ['showAlias', 'obfuscateRanking', 'showRanking'];
  private DEFAULT_RANKING_CONFIG = this.RANKING_CONFIGS[2];

  public checkedBonusCourse$: BehaviorSubject<boolean>;
  protected questions: QuestionBase<any>[];
  protected groupDataQuestionGroup: QuestionGroup;
  protected groupConfigQuestionGroup: QuestionGroup;
  protected groupScheduleQuestionGroup: QuestionGroup;
  protected agentsQuestionGroup: QuestionGroup;
  protected bonusDataQuestionGroup: QuestionGroup;
  protected questionsSubject = new BehaviorSubject(null);
  protected formData: any = {};
  protected timezoneSubject = new BehaviorSubject(null);
  protected minDateSubject = new BehaviorSubject(null);
  protected maxDateSubject = new BehaviorSubject(null);
  protected maxWeeks: number = this.DEFAULT_MIN_WEEKS;
  protected form: UntypedFormGroup;
  protected userRole: any;
  protected roleName: any;

  constructor(
    protected factory: CoreQuestionsFactoryService,
    protected countries: CountriesService,
    protected translateService: TranslateService,
    protected profileService: ProfileService,
    protected agentsCache: AgentsCacheService,
    protected coursesService: CoursesService,
    private utilsQuestionsService: UtilsQuestionsService,
    private cd: ChangeDetectorRef
  ) {
    this.profileService.getLoggedUserRole$().subscribe((userRole) => {
      this.userRole = userRole;
      this.roleName = userRole.name;
    });
    this.checkedBonusCourse$ = new BehaviorSubject<boolean>(false);
  }

  setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  getFormData(): any {
    return null;
  }

  onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    const { group } = params;
    this.questions = [];
    this.onDataChanged();
    this.minDateSubject.next(group?.startDate ? new Date(group.startDate) : null);
    this.maxDateSubject.next(group?.endDate ? new Date(group.endDate) : null);
    this.setMaxWeeks(params);
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  createQuestions(params: any = {}) {
    const questions = [this.getGroupDataQuestions(params), this.getGroupConfigQuestions(params)];
    const editable = !params.client?.scorm?.enabled;
    if (this.userRole.scope !== 'partner') {
      questions.push(this.getAgentsQuestionGroup(params, editable));
    }
    this.questions = questions.filter((question) => question != null);
    this.questionsSubject.next(this.questions);
  }

  setMaxWeeks({ group }) {
    this.maxWeeks = group?.weeks || this.DEFAULT_MIN_WEEKS;
    const startDate = this.minDateSubject.getValue();
    const endDate = this.maxDateSubject.getValue();
    if (startDate && endDate) {
      const maxMilliseconds = endDate - startDate;
      // Convert from milliseconds to weeks
      this.maxWeeks = Math.ceil(maxMilliseconds * 0.000000001653439);
      if (this.form && this.form.controls['weeks']) {
        const weeks = Math.min(this.maxWeeks, this.DEFAULT_MAX_WEEKS);
        this.form.controls['weeks'].setValue(weeks);
      }
    }
  }

  async onClientSelected(client, params) {
    this.agentsCache.updateClientAgents(client.id);
    this.agentsCache.updateDistributorAgents(client.distributorId);
    await this.coursesService.loadClientCatalogueCache(client.id, true);

    // Set the course question
    const { childQuestions } = this.groupDataQuestionGroup;
    const coursesQuestionIndex = this.utilsQuestionsService.findQuestionIndex('customizationId', childQuestions);
    const newCourseQuestion = this.getCourseQuestion({ ...params, client });
    childQuestions[coursesQuestionIndex] = newCourseQuestion;

    this.form.controls['country'].setValue(client.countryId);
    params.client = client;
  }

  onCourseSelected(course, params) {
    if (course.type == 'itinerary') {
      this.form.controls['modules'].setValue(
        course.modules.map((module) => {
          return {
            courseId: course.id,
            gameId: module.gameId,
            gameName: module.gameName,
            hours: module.hours,
            minutes: module.minutes
          };
        })
      );
      this.form.controls['type'].setValue('itinerary');
      this.form.controls['gameId'].setValue(null);
    } else {
      this.form.controls['type'].setValue('regular');
      this.form.controls['modules'].setValue(null);
      this.form.controls['gameId'].setValue(course.gameId);
    }

    // Set the bonus checkbox
    if (this.userRole.scope !== 'partner') {
      const { childQuestions } = this.groupDataQuestionGroup;
      const bonusQuestionIndex = this.utilsQuestionsService.findQuestionIndex('hasBonus', childQuestions);
      const newBonusQuestion = this.getBonusQuestion({ ...params, course });
      childQuestions[bonusQuestionIndex] = newBonusQuestion;
    }
  }

  /**
   * Set the selected country, timezone and bonus that depends on client
   * @param country key
   * @param params data
   */
  private onChangeCountry(country: string, params: any): void {
    const { childQuestions } = this.groupDataQuestionGroup;
    // Set the timezone dropdown
    const timezone = this.countries.getDefaultTimezone(country);
    const timezoneQuestion = this.groupDataQuestionGroup.getChildQuestion('timezone');
    timezoneQuestion.options = this.countries.getTimezoneOptions(country);
    timezoneQuestion.control.setValue(timezone);

    // Set the bonus checkbox
    if (this.userRole.scope !== 'partner') {
      const bonusQuestionIndex = this.utilsQuestionsService.findQuestionIndex('hasBonus', childQuestions);
      const newBonusQuestion = this.getBonusQuestion({ ...params, country });
      childQuestions[bonusQuestionIndex] = newBonusQuestion;
      this.onBonusFlagChanged(false, params);
    }
  }

  /**
   * Set the value of bonus class
   * @param isBonus
   */
  setBonusQuestion(isBonus: boolean) {
    const hasBonusQuestion = this.utilsQuestionsService.findQuestion('hasBonus', this.questions);
    if (hasBonusQuestion) {
      hasBonusQuestion.control.setValue(isBonus);
      this.cd.detectChanges();
    }
  }

  onBonusFlagChanged(hasBonus: boolean, params) {
    const { group } = params;
    if (group) {
      group.hasBonus = hasBonus;
    }
    const groupHasAlreadyStarted = group?.startDate && new Date(group.startDate).getTime() < new Date().getTime();
    params.hasBonus = hasBonus;
    params.agentIds = this.form.controls['agentIds'].value; //this.agentsQuestionGroup.getChildQuestion('agentIds').control.value;
    const newAgentsQuestion = this.getAgentsQuestion(params, true);
    this.agentsQuestionGroup.childQuestions = [newAgentsQuestion];

    // Disabled notifications
    const notificationsCfgIndex = this.utilsQuestionsService.findQuestionIndex(
      'notificationsCfg',
      this.groupConfigQuestionGroup.childQuestions
    );
    this.groupConfigQuestionGroup.childQuestions.splice(
      notificationsCfgIndex,
      1,
      this.getNotificationsConfig(params, hasBonus)
    );

    // Disabled extension mode
    const extensionModeIndex = this.utilsQuestionsService.findQuestionIndex(
      'extensionMode',
      this.groupConfigQuestionGroup.childQuestions
    );
    const groupForExtension = group ? group : { hasBonus };
    this.groupConfigQuestionGroup.childQuestions.splice(
      extensionModeIndex,
      1,
      this.getExtensionModeQuestion({ ...params, group: groupForExtension })
    );

    // Hidden bonus data
    this.bonusDataQuestionGroup.hidden = !hasBonus;
    this.bonusDataQuestionGroup.setDisabled(!hasBonus);
    (this.bonusDataQuestionGroup.getChildQuestion('bonusData') as QuestionGroup).isOpenCollapsible = hasBonus;
    this.bonusDataQuestionGroup.getChildQuestion('bonusData').getChildQuestion('originalEndDate').setDisabled(true);
    this.groupScheduleQuestionGroup.setEditable(!hasBonus);

    if (
      !(this.userRole.group === 'cs' || this.userRole.name === 'superAdmin') &&
      !groupHasAlreadyStarted &&
      (!group || !group.hasBonus)
    ) {
      const { childQuestions } = this.groupConfigQuestionGroup;
      const startDateQuestionIndex = this.utilsQuestionsService.findQuestionIndex('startDate', childQuestions);
      const endDateQuestionIndex = this.utilsQuestionsService.findQuestionIndex('endDate', childQuestions);
      const weeksQuestionIndex = this.utilsQuestionsService.findQuestionIndex('weeks', childQuestions);
      if (hasBonus) {
        // A class must start at least 10 days after it is marked as bonus
        this.minDateSubject.next(new Date(Date.now() + Date.UTC(1970, 0, 10)));
      } else {
        this.minDateSubject.next(null);
      }
      this.maxDateSubject.next(null);
      this.setMaxWeeks(params);
      childQuestions[startDateQuestionIndex] = this.getStartDateQuestion(params, groupHasAlreadyStarted);
      childQuestions[endDateQuestionIndex] = this.getEndDateQuestion(params, true);
      childQuestions[weeksQuestionIndex] = this.getWeeksQuestion(params);
    }
    this.cd.detectChanges();
  }

  getGroupDataQuestions(params: any): QuestionBase<any> {
    this.groupDataQuestionGroup = new QuestionGroup({
      title: 'groups.info',
      isSection: true,
      childQuestions: this.createGroupDataQuestions(params).filter((question) => question != null)
    });
    return this.groupDataQuestionGroup;
  }

  createGroupDataQuestions(params: any = {}): QuestionBase<any>[] {
    const { group } = params;
    const client = !params.client && group ? { id: group.clientId, name: group.clientName } : params.client;
    const country = group?.country || client?.countryId || null;
    const timezone = group?.timezone || client?.timezone || null;
    const editable = !params.client?.scorm?.enabled;
    Object.assign(params, { client });

    return [
      this.getClientQuestion(params, country, timezone),
      new TextboxQuestion({
        key: 'name',
        label: 'groups.name',
        class: 'col-1',
        editable,
        value: group?.name || this.form?.controls['name']?.value || this.formData.name,
        validators: { required: true }
      }),
      this.getTopicQuestion(params, editable),
      this.getCountryQuestion(params, country, editable),
      this.getTimezoneQuestion(country, timezone, editable),
      this.getCourseQuestion(params),
      this.getModulesQuestion(params),
      this.getTypeQuestion(params),
      this.getGameIdQuestion(params),
      this.getBonusQuestion(params),
      this.getBonusDataQuestionGroup(params)
    ];
  }

  getClientQuestion(params, country, timezone): QuestionBase<any> {
    const { group, client } = params;
    return this.factory.getClientQuestion({
      ...params,
      class: 'col-1',
      validators: { required: true },
      editable: !client,
      // disabled: group ? true : false,
      childQuestions: group
        ? []
        : [
            {
              key: 'country',
              value: country,
              source: 'countryId'
            },
            {
              key: 'timezone',
              value: timezone,
              source: 'timezone'
            }
          ],
      eventHandlers: {
        onSelected: async (clientSelected) => await this.onClientSelected(clientSelected, params)
      }
    });
  }

  getCourseQuestion(params) {
    const { group, course } = params;
    const courseId = group?.customizationId || (params.client && course?.id) || null;
    // on edit group only one option is needed
    const courses =
      group && course
        ? [
            {
              key: course.id,
              value: course.title || course.name,
              data: course
            }
          ]
        : this.coursesService.getCoursesItinerariesOptions(true);
    const courseQuestion = new DropdownQuestion({
      key: 'customizationId',
      options: courses,
      class: 'col-1',
      disabled: group || !params.client,
      placeholder: 'actions.selectCourse',
      label: 'nouns.course',
      value: courseId,
      validators: { required: true },
      eventHandlers: {
        onSelected: async (courseSelected) => await this.onCourseSelected(courseSelected, params)
      }
    });
    return courseQuestion;
  }

  getModulesQuestion(params) {
    const { group, course } = params;
    const modules = group?.modules || course?.modules || null;
    const modulesQuestion = new HiddenQuestion({
      key: 'modules',
      value: modules
    });
    return modulesQuestion;
  }

  getTypeQuestion(params) {
    const { group, course } = params;
    const type = group?.type || course?.type || null;
    const typeQuestion = new HiddenQuestion({
      key: 'type',
      value: type
    });
    return typeQuestion;
  }

  getGameIdQuestion(params) {
    const { group, course } = params;
    const gameId = group?.gameId || course?.gameId || null;
    const gameIdQuestion = new HiddenQuestion({
      key: 'gameId',
      value: gameId
    });
    return gameIdQuestion;
  }

  getTopicQuestion(params, editable) {
    const { group } = params;
    return new TextboxQuestion({
      key: 'topic',
      label: 'groups.topic',
      class: 'col-1',
      value: group?.topic || this.formData.topic,
      editable,
      validators: { required: false }
    });
  }

  getCountryQuestion(params, country, editable) {
    return this.factory.getCountryQuestion({
      ...params,
      class: 'col-2',
      value: country,
      validators: { required: true },
      editable,
      eventHandlers: {
        onChange: (value) => {
          this.onChangeCountry(value, params);
        }
      }
    });
  }

  getTimezoneQuestion(country, timezone, editable) {
    this.timezoneSubject.next(timezone);
    return this.factory.getTimezoneQuestion({
      country,
      class: 'col-2',
      value: timezone,
      validators: { required: true },
      editable,
      eventHandlers: {
        onChange: (value) => {
          this.timezoneSubject.next(value);
        }
      }
    });
  }

  getBonusQuestion(params: any) {
    const { clientId = params.group?.clientId || this.formData.clientId, group, client, country } = params;
    const newCountry = country || group?.country || this.formData.country;
    params = { ...params, newCountry };
    const course =
      params.course ||
      this.coursesService.getCourse(clientId, group ? group.customizationId : this.formData.customizationId);

    const isClientSelected = !!client;
    const groupHasAlreadyStarted = group?.startDate && new Date(group.startDate).getTime() < new Date().getTime();
    const disabled =
      !isClientSelected ||
      groupHasAlreadyStarted ||
      !course ||
      !course.bonusPossible ||
      newCountry !== 'ESP' ||
      (!!group && this.userRole.scope !== 'global' && this.userRole.scope !== 'root');
    const hidden = !isClientSelected || !course || !course.bonusPossible || newCountry !== 'ESP';

    if (!group && this.formData.hasBonus && disabled) {
      params.hasBonus = false;
      this.onBonusFlagChanged(false, params);
    }

    return new CheckboxQuestion({
      key: 'hasBonus',
      class: 'col-1',
      label: 'groups.bonusClass',
      disabled,
      hidden,
      value: group?.hasBonus,
      eventHandlers: {
        onChange: (value) => {
          this.setBonusQuestion(value);
          this.onBonusFlagChanged(value, params);
          this.checkedBonusCourse$.next(value);
        }
      }
    });
  }

  getGroupConfigQuestions(params: any): QuestionBase<any> {
    const { group } = params;
    const groupHasAlreadyStarted = group?.startDate && new Date(group.startDate).getTime() < new Date().getTime();
    const editable = !params.client?.scorm?.enabled;
    this.groupConfigQuestionGroup = new QuestionGroup({
      title: 'groups.config.title',
      isSection: true,
      childQuestions: [
        this.getStartDateQuestion(params, groupHasAlreadyStarted),
        this.getEndDateQuestion(params, editable),
        this.getWeeksQuestion(params),
        this.getExtensionModeQuestion(params),
        this.getNotificationsConfig(params),
        new Decorator({ separator: true }),
        this.getGroupAdvancedConfigQuestions(params)
      ]
    });

    return this.groupConfigQuestionGroup;
  }

  getStartDateQuestion(params, groupHasAlreadyStarted) {
    const { group } = params;
    const minDateIfNotStarted =
      params.hasBonus && this.minDateSubject.getValue()
        ? DateTime.fromJSDate(new Date(this.minDateSubject.getValue())).toJSDate()
        : DateTime.local().set({ hour: 0, minute: 0, second: 0, millisecond: 0 }).toJSDate();
    const startDate = new DatetimeQuestion({
      key: 'startDate',
      label: 'nouns.startDate',
      class: 'col-2',
      timezone: this.timezoneSubject.asObservable(),
      maxDate: this.maxDateSubject.asObservable(),
      minDate: groupHasAlreadyStarted ? DateTime.fromJSDate(new Date(group.startDate)).toJSDate() : minDateIfNotStarted,
      editable: !group?.hasBonus && !groupHasAlreadyStarted,
      disabled: groupHasAlreadyStarted,
      value: group?.startDate || null,
      validators: groupHasAlreadyStarted ? {} : { required: true, invalidDate: true },
      eventHandlers: groupHasAlreadyStarted
        ? {}
        : {
            onChange: (groupStartDate) => {
              if (groupStartDate && groupStartDate instanceof Date && !isNaN(groupStartDate.getTime())) {
                this.minDateSubject.next(groupStartDate);
                const groupEndDate = DateTime.fromJSDate(groupStartDate)
                  .plus({ weeks: this.DEFAULT_MIN_WEEKS })
                  .minus({ second: 1 })
                  .toJSDate();
                startDate.form.controls['endDate'].setValue(groupEndDate);
                this.maxDateSubject.next(groupEndDate);
                this.setMaxWeeks(params);
              }
            }
          }
    });
    return startDate;
  }

  getEndDateQuestion(params, editable) {
    const { group } = params;
    const groupEndDate = group?.endDate || null;
    const isAutomaticGroup = group?.automatic;
    const isFundaeGroup = group?.hasBonus;
    const groupHasAlreadyEnded =
      group?.originalEndDate && new Date(group.originalEndDate).getTime() < new Date().getTime();
    const endDate = new DatetimeQuestion({
      key: 'endDate',
      label: 'nouns.endDate',
      class: 'col-2',
      editable: !isAutomaticGroup && (!isFundaeGroup || groupHasAlreadyEnded) && editable,
      timezone: this.timezoneSubject.asObservable(),
      minDate: this.minDateSubject,
      defaultHour: 23,
      defaultMinute: 59,
      defaultSeconds: 59,
      value: group?.automatic ? null : groupEndDate,
      validators: { required: !isAutomaticGroup, invalidDate: !isAutomaticGroup },
      eventHandlers: {
        onChange: (groupEndDate) => {
          this.maxDateSubject.next(groupEndDate);
          this.setMaxWeeks(params);
        }
      }
    });
    return endDate;
  }

  getWeeksQuestion(params) {
    const { group } = params;
    const groupHasAlreadyStarted = group?.startDate && new Date(group.startDate).getTime() < new Date().getTime();
    let validators = {};
    if (!groupHasAlreadyStarted) {
      validators = {
        number: true,
        required: true,
        function: (formControl: UntypedFormControl) => {
          const maxWeeks = Math.min(this.maxWeeks, this.WEEKS_IN_A_YEAR);
          const weeks = formControl.value;
          if (weeks > maxWeeks) {
            return { maxPlayTime: { limit: maxWeeks } };
          }
          return null;
        }
      };
    }
    return new NumberQuestion({
      key: 'weeks',
      class: 'col-2-offset',
      label: 'groups.weeks',
      editable: !groupHasAlreadyStarted,
      value: group?.weeks || Math.min(this.maxWeeks, this.DEFAULT_MAX_WEEKS),
      validators
    });
  }

  getExtensionModeQuestion(params): QuestionBase<any> {
    const { group } = params;
    return new CheckboxQuestion({
      key: 'extensionMode',
      class: 'col-1',
      label: 'groups.playTime.extension.mode.automatic',
      editable: !params?.client?.scorm?.enabled,
      disabled: group?.hasBonus,
      value: group?.hasBonus ? 'disabled' : group?.extensionMode || 'disabled',
      checkedValue: 'automatic',
      uncheckedValue: 'disabled'
    });
  }

  getNotificationsConfig(params, hasBonus: boolean = false): QuestionBase<any> {
    const { group } = params;
    const notificationsCfg = group?.notificationsCfg || { n2: { enabled: true } };
    return new QuestionGroup({
      key: 'notificationsCfg',
      class: 'col-1',
      childQuestions: [
        new QuestionGroup({
          key: 'n2',
          class: 'col-1',
          childQuestions: [
            new CheckboxQuestion({
              key: 'enabled',
              tooltipKey: 'notificationsCfg.n2.enabled',
              class: 'col-1',
              label: 'notifications.config.startClass',
              value: hasBonus || notificationsCfg.n2.enabled,
              editable: !params?.client?.scorm?.enabled,
              disabled: group?.hasBonus || hasBonus,
              validators: { required: false }
            })
          ]
        })
      ]
    });
  }

  getGroupAdvancedConfigQuestions(params: any): QuestionBase<any> {
    const editable = !params.client?.scorm?.enabled;
    return new QuestionGroup({
      title: 'groups.config.advancedConfig',
      isDropdown: true,
      childQuestions: [
        this.getRankingQuestion(params),
        this.getTagsQuestion(params, editable),
        this.getScheduleQuestions(params),
        this.getDocumentsQuestions(params)
      ]
    });
  }

  getRankingQuestion(params) {
    const rankingType = this.getRankingQuestionValue(params);

    return new DropdownQuestion({
      key: 'rankingConfig',
      label: 'properties.rankingConfig',
      class: 'col-2-offset',
      value: rankingType,
      options: [
        { key: 'showRanking', value: this.translateService.instant('properties.showRanking') },
        { key: 'obfuscateRanking', value: this.translateService.instant('properties.obfuscateRanking') },
        { key: 'showAlias', value: this.translateService.instant('properties.showAlias') }
      ]
    });
  }

  getRankingQuestionValue(params): string {
    const { group, client } = params;

    return group?.flags
      ? this.RANKING_CONFIGS.find((c) => group.flags[c]) || this.DEFAULT_RANKING_CONFIG
      : client?.ranking
      ? this.RANKING_CONFIGS.find((c) => client.ranking[c]) || this.DEFAULT_RANKING_CONFIG
      : this.DEFAULT_RANKING_CONFIG;
  }

  getTagsQuestion(params, editable): QuestionBase<any> {
    const { group } = params;
    return new TagsQuestion({
      key: 'tags',
      label: 'nouns.tags',
      class: 'col-1',
      editable,
      value: group?.tags || []
    });
  }

  getScheduleQuestions(params: any = {}) {
    const weekDays = Info.weekdaysFormat('long', { locale: 'en' }).map((day) => day.toLowerCase());
    const localeWeekDays = Info.weekdaysFormat('short').map((day) => `${day[0].toUpperCase()}${day.slice(1)}`);
    this.groupScheduleQuestionGroup = new QuestionGroup({
      key: 'hoursRestrictions',
      childQuestions: [
        new Decorator({
          value: 'groups.config.schedule.label',
          tooltip: 'groups.config.schedule.info',
          tooltipKey: 'groups.config.schedule',
          valueClass: 'heading-form',
          class: 'no-margin'
        }),
        ...weekDays.map((weekday, index) => this.getWeekdayQuestion(params, weekday, localeWeekDays[index]))
      ]
    });
    return this.groupScheduleQuestionGroup;
  }

  getWeekdayQuestion(params, weekday, label) {
    const { group, hasBonus = false } = params;
    const bonusGroup = group?.hasBonus || hasBonus;
    const hoursRestrictions = group?.hoursRestrictions || {};
    const weekdayConfig = hoursRestrictions[weekday] || { allowed: true };
    const start = weekdayConfig.start || '00:00';
    const end = weekdayConfig.finish || '24:00';
    const weekdayGroup = new QuestionGroup({
      key: weekday,
      gridClass: 'grid-columns-12'
    });
    const _rangeQuestion = new RangeQuestion({
      key: '_timerange',
      class: 'grid-columns-area-11',
      editable: !bonusGroup,
      value: [start, end],
      config: {
        type: 'double',
        keyboard: true,
        grid: true,
        min: 0,
        max: 24 * 60,
        min_interval: 60,
        block: !weekdayConfig.allowed,
        step: weekdayConfig.allowed ? 30 : 0,
        from: weekdayConfig.allowed ? this._toMinutes(start) : 0,
        to: weekdayConfig.allowed ? this._toMinutes(end) : 0,
        hide_from_to: !weekdayConfig.allowed,
        prettify: (num) =>
          `${Math.floor(num / 60)
            .toString()
            .padStart(2, '0')}:${(num % 60).toString().padStart(2, '0')}`
      },
      eventHandlers: {
        onSelected: (value) => {
          (weekdayGroup.control as UntypedFormGroup).controls['start'].setValue(value[0]);
          (weekdayGroup.control as UntypedFormGroup).controls['finish'].setValue(value[1]);
        }
      }
    });

    weekdayGroup.childQuestions = [
      new CheckboxQuestion({
        label,
        small: true,
        key: 'allowed',
        class: 'grid-columns-area-1',
        editable: !bonusGroup,
        value: !!weekdayConfig.allowed,
        eventHandlers: {
          onChange: (checked) => {
            const options = {
              block: !checked,
              step: checked ? 30 : 0,
              from: 0,
              to: checked ? 24 * 60 : 0,
              hide_from_to: !checked
            };
            const defaultRange = ['00:00', '24:00'];
            _rangeQuestion.options.config.slider.update(options);
            (weekdayGroup.control as UntypedFormGroup).controls['start'].setValue(defaultRange[0]);
            (weekdayGroup.control as UntypedFormGroup).controls['finish'].setValue(defaultRange[1]);
          }
        }
      }),
      new HiddenQuestion({
        key: 'start',
        value: start
      }),
      new HiddenQuestion({
        key: 'finish',
        value: end
      }),
      _rangeQuestion
    ];
    return weekdayGroup;
  }
  _toMinutes(time: string): number {
    const hhmm = time.split(':');
    return parseInt(hhmm[0], null) * 60 + parseInt(hhmm[1], null);
  }

  getDocumentsQuestions(params: any = {}) {
    const { group } = params;
    const documentation = group?.documentation || {};
    const documentationQuestion = new QuestionGroup({
      key: 'documentation',
      childQuestions: [
        new Decorator({
          value: 'groups.config.documentation.title',
          tooltip: 'groups.config.documentation.info',
          tooltipKey: 'groups.config.documentation',
          valueClass: 'heading-form',
          class: 'no-margin'
        }),
        new HiddenQuestion({
          key: 'hasChanges',
          value: false
        }),
        new HiddenQuestion({
          key: 'type',
          value: documentation.type
        }),
        new DocumentsUploader({
          key: '_document',
          maxSizeInMB: 5,
          value: documentation.type === 'pdf' ? documentation.url : '',
          disabled: documentation.type === 'url',
          eventHandlers: {
            onSelected: (value) => {
              (documentationQuestion.control as UntypedFormGroup).controls['hasChanges'].setValue(true);
              (documentationQuestion.control as UntypedFormGroup).controls['type'].setValue('pdf');
              (documentationQuestion.control as UntypedFormGroup).controls['url'].setValue(value?.name || '');
              documentationQuestion.getChildQuestion('url').editable = !value;
            }
          }
        }),
        new TextboxQuestion({
          key: 'url',
          placeholder: 'actions.uploadUrlDocument',
          class: 'col-1',
          value: documentation.url,
          iconClass: documentation.type === 'url' ? 'glicon-close' : '',
          editable: documentation.type && documentation.type === 'url',
          eventHandlers: {
            onClick: (value, question) => {
              question.control.setValue(null);
              question.iconClass = '';
              documentationQuestion.getChildQuestion('_document').disabled = false;
              (documentationQuestion.control as UntypedFormGroup).controls['hasChanges'].setValue(true);
              (documentationQuestion.control as UntypedFormGroup).controls['type'].setValue('url');
            },
            onKeyup: (value, question) => {
              if (question.editable) {
                question.iconClass = value ? 'glicon-close1' : '';
                (documentationQuestion.control as UntypedFormGroup).controls['hasChanges'].setValue(true);
                (documentationQuestion.control as UntypedFormGroup).controls['type'].setValue('url');
                documentationQuestion.getChildQuestion('_document').disabled = !!value;
              }
            }
          }
        })
      ]
    });
    return documentationQuestion;
  }

  getAgentsQuestionGroup(params, editable) {
    this.agentsQuestionGroup = new QuestionGroup({
      title: 'groups.agents',
      isSection: true,
      isDropdown: this.userRole.scope !== 'client',
      childQuestions: [this.getAgentsQuestion(params, editable)]
    });
    return this.agentsQuestionGroup;
  }

  getAgentsQuestion(params, editable) {
    const { group, client, hasBonus = false, agentIds = [] } = params;
    const clientId = group?.clientId || client?.id || null;
    const distributorId = group?.distributorId || client?.distributorId || null;
    const agentsList = group?.agentIds || agentIds;
    this.agentsCache.updateClientAgents(clientId);
    this.agentsCache.updateDistributorAgents(distributorId);

    return new QuestionGroup({
      key: 'agentIds',
      controlType: 'agents',
      value: agentsList,
      childQuestions: [
        this.factory.getAgentQuestion({
          key: 'cs',
          value: agentsList,
          label: 'nouns.cs',
          class: 'col-1',
          group,
          client,
          editable,
          hasBonus,
          role: ROLES.CS
        }),
        this.factory.getAgentQuestion({
          key: 'salesman',
          value: agentsList,
          label: 'nouns.salesmans',
          class: 'col-1',
          group,
          client,
          editable,
          hasBonus,
          role: ROLES.SALES
        }),
        this.factory.getAgentQuestion({
          key: 'distributors',
          value: agentsList,
          label: 'nouns.distributors',
          class: 'col-1',
          group,
          client,
          hasBonus,
          role: ROLES.DISTRIBUTOR,
          disabled: this.roleName === 'distributor',
          editable: this.roleName !== 'distributor' && editable
        }),
        this.getHrQuestion(params, hasBonus, editable)
      ].filter((question) => question !== null)
    });
  }

  getHrQuestion(params, hasBonus, editable) {
    const { group, client, agentIds = [] } = params;
    const clientId = group?.clientId || client?.id || null;
    const agentsList = group?.agentIds || agentIds;
    this.agentsCache.updateClientAgents(clientId);

    const question = this.factory.getAgentQuestion({
      key: 'hr',
      value: agentsList,
      label: 'nouns.hrname',
      class: `col-1`,
      group,
      client,
      disabled: this.roleName === 'distributor',
      editable: this.roleName !== 'distributor' && editable,
      required: hasBonus,
      validators: {
        required: hasBonus
      },
      role: ROLES.HR
    });

    return question;
  }

  getBonusDataQuestionGroup(params: any): QuestionBase<any> {
    const { group, hasBonus = false } = params;
    const bonusGroup = group?.hasBonus || hasBonus;
    this.bonusDataQuestionGroup = new QuestionGroup({
      hidden: !bonusGroup,
      childQuestions: [new Decorator({ separator: true }), this.getBonusDataQuestions(params)]
    });
    return this.bonusDataQuestionGroup;
  }

  getBonusDataQuestions(params: any) {
    const { group, hasBonus = false } = params;
    const client = params.client || {};
    const bonusGroup = group?.hasBonus || hasBonus;
    const editable = this.userRole.group === 'cs' || this.userRole.name === 'superAdmin';
    const bonusData = group?.bonusData || {};
    const groupHasAlreadyStarted = group?.startDate && new Date(group.startDate).getTime() < new Date().getTime();
    const editableClientAdmin = this.userRole.name === 'clientAdmin' || this.userRole.name === 'dachAdmin' || editable;
    const editableHrAdmin =
      editableClientAdmin && bonusData.declaredStudents ? bonusData.declaredStudents === '' : true;
    const isDeclaredStudentsEditable = editableHrAdmin || !groupHasAlreadyStarted;

    return new QuestionGroup({
      title: 'groups.bonus.title',
      key: 'bonusData',
      disabled: !bonusGroup,
      isDropdown: true,
      childQuestions: [
        new TextboxQuestion({
          key: 'tutor',
          label: 'nouns.tutor',
          class: 'col-2',
          editable,
          disabled: !bonusGroup,
          value: bonusData.tutor,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'tutorPid',
          label: 'nouns.tutorPid',
          class: 'col-2',
          disabled: !bonusGroup,
          editable,
          value: bonusData.tutorPid,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'tutorEmail',
          label: 'nouns.tutorEmail',
          class: 'col-2',
          disabled: !bonusGroup,
          editable,
          value: bonusData.tutorEmail || 'support@game-learn.com',
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'cid',
          label: 'nouns.cid',
          class: 'col-2',
          disabled: !bonusGroup,
          value: bonusData.cid || client.cid,
          validators: { required: true }
        }),
        new TextboxQuestion({
          key: 'companyName',
          label: 'nouns.companyName',
          disabled: !bonusGroup,
          class: 'col-2',
          editable: editableClientAdmin,
          value: bonusData.companyName || client.name,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'tutorialTime',
          label: 'nouns.tutorialTime',
          class: 'col-2',
          disabled: !bonusGroup,
          editable,
          value: bonusData.tutorialTime,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'forumUrl',
          label: 'nouns.forumUrl',
          class: 'col-2',
          disabled: !bonusGroup,
          editable,
          value: bonusData.forumUrl,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'forumPassword',
          label: 'nouns.password',
          class: 'col-2',
          disabled: !bonusGroup,
          editable,
          value: bonusData.forumPassword,
          validators: { required: editable }
        }),
        new TextboxQuestion({
          key: 'caf',
          label: 'nouns.caf',
          disabled: !bonusGroup,
          class: 'col-2',
          value: bonusData.caf
        }),
        new TextboxQuestion({
          key: 'group',
          label: 'nouns.bonusGroup',
          disabled: !bonusGroup,
          class: 'col-2',
          value: bonusData.group
        }),
        new DatetimeQuestion({
          key: 'originalEndDate',
          label: 'groups.originalEndDate',
          class: 'col-2',
          timezone: this.timezoneSubject.asObservable(),
          value: group?.originalEndDate || null,
          disabled: true
        }),
        new TextboxQuestion({
          key: 'declaredStudents',
          label: 'nouns.declaredStudents',
          class: 'col-2',
          disabled: !bonusGroup,
          editable: isDeclaredStudentsEditable,
          value: bonusData.declaredStudents,
          validators: {
            required: editableHrAdmin,
            number: true,
            // Older groups exist with now invalid declaredStudents values
            ...(isDeclaredStudentsEditable
              ? {
                  minValue: {
                    limit: 10
                  },
                  maxValue: {
                    limit: 80
                  }
                }
              : {})
          }
        })
      ]
    });
  }
}
